// FrequencyPerWeekAutocomplete.tsx
import React, { useEffect, useState } from "react";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { TextField } from "@material-ui/core";
import { useStyles } from "./styleUtils";

const frequencyStringList: string[] = [
  "Everyday",
  "Once a week",
  "Twice a week",
  "Thrice a week",
  "Four times a week",
  "Five times a week",
  "Six times a week",
  "SOS",
  "Alternate days",
  "Once a month",
  "Saturday",
  "Sunday",
  "Monday and Thursday",
  "Monday to Friday",
  "Four times a day",
  "Saturday and Sunday",
  "Monday",
  "Tuesday to Friday",
];

type Props = {
  value?: string | null;
  onSelectHandler: (value: string | null) => void;
  onTextChangeHandler?: (value: string) => void;
  isDisabled?: boolean;
};

const FrequencyPerWeekAutocomplete: React.FC<Props> = ({
  value = "",
  onSelectHandler,
  onTextChangeHandler = () => {},
  isDisabled = false,
}) => {
  const classes = useStyles();
  const [freqSuggestions, setFreqSuggestions] =
    useState<string[]>(frequencyStringList);
  const [localValue, setLocalValue] = useState<string | undefined>(
    value ?? undefined
  );
  const [isOpen, setIsOpen] = useState<boolean>(false);

  useEffect(() => {
    setLocalValue(value ?? undefined);
  }, [value]);

  useEffect(() => {
    const sval = localValue ?? "";
    // If empty or looks like a number (user typed numeric), show full list
    if (sval.trim() === "" || !isNaN(Number(sval))) {
      setFreqSuggestions(frequencyStringList);
    } else {
      const q = sval.toLocaleLowerCase();
      setFreqSuggestions(
        frequencyStringList.filter((frequency) =>
          frequency.toLocaleLowerCase().includes(q)
        )
      );
    }
  }, [localValue]);

  return (
    <Autocomplete
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      id="frequency-per-week-autocomplete"
      options={freqSuggestions}
      getOptionLabel={(option: string) => option}
      filterOptions={(x) => x} // we already filter in state
      disabled={isDisabled}
      value={localValue ?? null}
      onChange={(_, newValue: string | null) => {
        onSelectHandler(newValue);
        setLocalValue(newValue ?? undefined);
        setIsOpen(false);
      }}
      renderInput={(params) => (
        // using native input style similar to original; you can replace with TextField if preferred
        <div ref={params.InputProps.ref} style={{ width: "100%" }}>
          <input
            {...params.inputProps}
            className={classes.textField}
            placeholder={!isDisabled ? "Enter Frequency" : ""}
            value={localValue ?? ""}
            onChange={(e) => {
              setIsOpen(true);
              setLocalValue(e.target.value);
            }}
            onClick={() => setIsOpen((s) => !s)}
            onBlur={(e) => {
              onTextChangeHandler(e.target.value);
              // close dropdown slightly after blur to allow onChange from selecting
              setTimeout(() => setIsOpen(false), 100);
            }}
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "8px 10px",
              borderRadius: 4,
              border: "1px solid #ccc",
              outline: "none",
              fontSize: 14,
            }}
          />
        </div>
      )}
    />
  );
};

export default FrequencyPerWeekAutocomplete;
